/**
 * Textarea — themed multi-line input. Same look and Field behavior as Input
 * (label/hint/error, focus ring, error state), for the ~21 ad-hoc <textarea>s.
 */
import { forwardRef, useState, type TextareaHTMLAttributes, type ReactNode, type CSSProperties } from 'react';
import { Field } from './Field';
import { controlBase, focusedRing, errorBorder } from './field-styles';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Style for the surrounding Field wrapper (only when a label/hint/error is shown). */
  fieldStyle?: CSSProperties;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, fieldStyle, style, onFocus, onBlur, id, rows = 4, ...rest },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const control = (
    <textarea
      ref={ref}
      id={id}
      rows={rows}
      style={{
        ...controlBase,
        resize: 'vertical',
        ...(focused ? focusedRing : null),
        ...(error != null ? errorBorder : null),
        ...style,
      }}
      onFocus={(e) => {
        setFocused(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        onBlur?.(e);
      }}
      {...rest}
    />
  );
  if (label == null && hint == null && error == null) return control;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id} style={fieldStyle}>
      {control}
    </Field>
  );
});
